import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Check, Loader2, Pencil, RefreshCw, Search, X } from 'lucide-react';
import type { OrgMemberResponse } from './types';
import { sanitizeNpiInput, validateNpi } from '../../utils/npiValidation';

// ─── RoleBadges ──────────────────────────────────────────────────────────────

const ROLE_LABELS = [
  { flag: 'is_assignable_clinician' as const, label: 'Clinician' },
  { flag: 'is_case_manager' as const, label: 'Case Mgr' },
  { flag: 'is_approver' as const, label: 'Approver' },
  { flag: 'is_physician' as const, label: 'Physician' },
];

const RoleBadges: React.FC<{
  assignments: OrgMemberResponse['clinic_assignments'];
}> = React.memo(function RoleBadges({ assignments }) {
  const memberAssignments = assignments ?? [];
  const activeRoles = ROLE_LABELS.filter(({ flag }) =>
    memberAssignments.some(a => a[flag])
  );

  if (activeRoles.length === 0) {
    return <span className='text-sm text-muted-foreground'>&mdash;</span>;
  }

  return (
    <div className='flex gap-1 flex-wrap'>
      {activeRoles.map(({ flag, label }) => (
        <Badge key={flag} variant='outline' className='text-xs'>
          {label}
        </Badge>
      ))}
    </div>
  );
});

// ─── NpiCell ─────────────────────────────────────────────────────────────────

const NPI_CELL_CLASS = 'w-[184px] min-w-[184px] max-w-[184px]';

interface NpiCellProps {
  memberId: string | null | undefined;
  memberName: string;
  npi: string | null | undefined;
  onSave: (userId: string, npi: string) => Promise<boolean>;
  isSaving: boolean;
}

function NpiCell({
  memberId,
  memberName,
  npi,
  onSave,
  isSaving,
}: NpiCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(npi || '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setValue(npi || '');
      setValidationError(null);
    }
  }, [isEditing, npi]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click from opening dialog
    if (!memberId) return;
    setValue(npi || '');
    setValidationError(null);
    setIsEditing(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = sanitizeNpiInput(e.target.value).slice(0, 10);
    setValue(newValue);
    setValidationError(newValue.length < 10 ? null : validateNpi(newValue));
  };

  const handleBlur = () => {
    const sanitizedValue = sanitizeNpiInput(value);
    if (!sanitizedValue) {
      setValidationError(null);
    } else {
      setValidationError(validateNpi(sanitizedValue));
    }
    window.setTimeout(() => {
      if (isSaving) {
        return;
      }
      if (!editorRef.current?.contains(document.activeElement)) {
        handleCancel();
      }
    }, 0);
  };

  const handleSave = async () => {
    if (!memberId) return;
    const sanitizedValue = sanitizeNpiInput(value);
    const error = validateNpi(sanitizedValue);
    if (error) {
      setValidationError(error);
      return;
    }
    const success = await onSave(memberId, sanitizedValue);
    if (success) {
      setIsEditing(false);
      setValidationError(null);
    }
  };

  const handleCancel = () => {
    setValue(npi || '');
    setValidationError(null);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      void handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <TableCell
        className={NPI_CELL_CLASS}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div ref={editorRef} className='flex flex-col gap-0.5'>
          <div className='flex items-center gap-1'>
            <Input
              inputSize='sm'
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder='Enter NPI'
              className='w-[96px] tabular-nums'
              autoFocus
              disabled={isSaving}
            />
            <Button
              variant='ghost'
              size='icon'
              onClick={() => void handleSave()}
              disabled={isSaving}
              className='text-green-500'
              title='Save (Enter)'
            >
              <Check className='h-3.5 w-3.5' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              onClick={handleCancel}
              disabled={isSaving}
              className='text-red-500'
              title='Cancel (Esc)'
            >
              <X className='h-3.5 w-3.5' />
            </Button>
          </div>
          {validationError && (
            <span className='text-xs text-red-500'>{validationError}</span>
          )}
        </div>
      </TableCell>
    );
  }

  if (!memberId) {
    return (
      <TableCell className={NPI_CELL_CLASS}>
        {npi ? (
          <span className='tabular-nums'>{npi}</span>
        ) : (
          <span className='text-sm text-muted-foreground'>—</span>
        )}
      </TableCell>
    );
  }

  return (
    <TableCell
      className={NPI_CELL_CLASS}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Button
        variant='ghost'
        size='sm'
        onClick={handleClick}
        className='w-full justify-between font-normal'
        aria-label={`Edit NPI for ${memberName}`}
      >
        {npi ? (
          <span className='truncate tabular-nums'>{npi}</span>
        ) : (
          <span className='text-muted-foreground/50 text-xs'>Add NPI</span>
        )}
        <Pencil
          className='h-3.5 w-3.5 text-muted-foreground'
          aria-hidden='true'
        />
      </Button>
    </TableCell>
  );
}

// ─── MemberRow ───────────────────────────────────────────────────────────────

type MemberRowProps = {
  member: OrgMemberResponse;
  onClick: (member: OrgMemberResponse) => void;
  onSaveNpi: (userId: string, npi: string) => Promise<boolean>;
  savingNpiForUserId: string | null;
};

const MemberRow: React.FC<MemberRowProps> = React.memo(function MemberRow({
  member,
  onClick,
  onSaveNpi,
  savingNpiForUserId,
}) {
  const assignmentCount = (member.clinic_assignments ?? []).length;
  const memberName =
    [member.first_name, member.last_name].filter(Boolean).join(' ') ||
    member.email;

  return (
    <TableRow className='cursor-pointer' onClick={() => onClick(member)}>
      <TableCell className='font-medium'>
        {member.first_name} {member.last_name}
      </TableCell>

      <TableCell className='text-muted-foreground'>{member.email}</TableCell>

      <NpiCell
        memberId={member.roger_user_id}
        memberName={memberName}
        npi={member.npi}
        onSave={onSaveNpi}
        isSaving={savingNpiForUserId === member.roger_user_id}
      />

      <TableCell>
        {assignmentCount > 0 ? (
          <Badge variant='secondary'>
            {assignmentCount} clinic{assignmentCount !== 1 ? 's' : ''}
          </Badge>
        ) : (
          <span className='text-sm text-muted-foreground'>None</span>
        )}
      </TableCell>

      <TableCell>
        <RoleBadges assignments={member.clinic_assignments} />
      </TableCell>
    </TableRow>
  );
});

// ─── MembersTable ────────────────────────────────────────────────────────────

type MembersTableProps = {
  members: OrgMemberResponse[];
  isLoading: boolean;
  hasSearchQuery: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  onEditMember: (member: OrgMemberResponse) => void;
  onSaveNpi: (userId: string, npi: string) => Promise<boolean>;
  savingNpiForUserId: string | null;
};

export const MembersTable: React.FC<MembersTableProps> = React.memo(
  function MembersTable({
    members,
    isLoading,
    hasSearchQuery,
    searchQuery,
    onSearchChange,
    onRefresh,
    onEditMember,
    onSaveNpi,
    savingNpiForUserId,
  }) {
    return (
      <>
        {/* Header */}
        <div className='flex justify-between items-center'>
          <h1 className='text-2xl font-bold tracking-tight'>
            Clinician Manager
          </h1>
          <Button variant='outline' onClick={onRefresh} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <RefreshCw className='h-4 w-4' />
            )}
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className='flex flex-wrap gap-4 items-end'>
          <div className='flex-1 min-w-[200px] max-w-sm'>
            <label
              htmlFor='member-search'
              className='text-sm font-medium mb-1.5 block'
            >
              Search
            </label>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                id='member-search'
                placeholder='Search by name or email...'
                value={searchQuery}
                onChange={e => onSearchChange(e.target.value)}
                className='pl-9'
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            <span className='ml-3 text-muted-foreground'>
              Loading organization members...
            </span>
          </div>
        ) : members.length === 0 ? (
          <div className='text-center text-muted-foreground py-8'>
            <p>
              {hasSearchQuery
                ? 'No members match your search.'
                : 'No organization members found.'}
            </p>
          </div>
        ) : (
          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className={NPI_CELL_CLASS}>NPI</TableHead>
                  <TableHead>Clinics</TableHead>
                  <TableHead>Roles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map(member => (
                  <MemberRow
                    key={member.workos_user_id}
                    member={member}
                    onClick={onEditMember}
                    onSaveNpi={onSaveNpi}
                    savingNpiForUserId={savingNpiForUserId}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </>
    );
  }
);
